<script setup lang="ts">
import { PickupPoint } from '@caks/shared/types/types.api'
import { getMapToken } from '@caks/shared/api/user.route'

interface Props {
  lon: number
  lat: number
  points: Array<PickupPoint>
  desktop?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  desktop: false,
})

let map: mapkit.Map
const router = useRouter()

onMounted(async () => {
  const { data } = await getMapToken()
  const keyMap = data.mapkit_token

  // инитим скрипт эппл мэпкита
  const mapScript = document.createElement('script')
  mapScript.src = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js'
  mapScript.async = true
  mapScript.onload = () => {
    const tokenID = keyMap
    mapkit.init({
      authorizationCallback: (done: (token: string) => void) => {
        done(tokenID)
      },
    })

    // создаем метки на карте
    const landmarks: {
      coordinate: mapkit.Coordinate
      title: string
      phone: string
    }[] = props.points.map((el) => {
      return {
        coordinate: new mapkit.Coordinate(+el.lon, +el.lat),
        title: el.name,
        phone: el.phone,
        open_from: el.open_from,
        open_to: el.open_to,
        id: el.id,
      }
    })

    // Landmark annotation callout delegate
    const CALLOUT_OFFSET = new DOMPoint(-148, -78)
    const landmarkAnnotationCallout = {
      calloutElementForAnnotation: (annotation: mapkit.Annotation) => {
        return calloutForLandmarkAnnotation(annotation)
      },

      calloutAnchorOffsetForAnnotation: () => {
        return CALLOUT_OFFSET
      },

      calloutAppearanceAnimationForAnnotation: () => {
        return '.4s cubic-bezier(0.4, 0, 0, 1.5) 0s 1 normal scale-and-fadein'
      },
    }

    // создаем аннотации для меток на карте
    const annotations: mapkit.Annotation[] = landmarks.map((landmark, item) => {
      const annotation = new mapkit.ImageAnnotation(landmark.coordinate, {
        callout: landmarkAnnotationCallout,
        color: '#009E50',
        url: { 1: '/assets/images/map/pin.svg' },
      })
      annotation.landmark = landmark
      annotation.addEventListener('select', () => {
        // Добавляем обработчик клика
        if (!props.desktop) router.push({ path: `/delivery/point/${landmark.id}/info` })
      })

      return annotation
    })

    map = new mapkit.Map('map')
    // console.log(map, props.lat, props.lon)
    // map.setCenterAnimated(new mapkit.Coordinate(props.lat, props.lon))

    map.showItems(annotations)

    function calloutForLandmarkAnnotation(annotation: mapkit.Annotation): HTMLElement {
      const div = document.createElement('div')
      div.className = 'landmark'

      const title = div.appendChild(document.createElement('h1'))
      title.textContent = annotation.landmark.title

      const section = div.appendChild(document.createElement('section'))

      const workTime = section.appendChild(document.createElement('p'))
      workTime.className = 'work-time'
      workTime.textContent = `Время работы: ${annotation.landmark.open_from}-${annotation.landmark.open_to}`

      const phone = section.appendChild(document.createElement('p'))
      phone.className = 'phone'
      phone.textContent = `Телефон: ${annotation.landmark.phone}`

      return div
    }
  }

  document.head.appendChild(mapScript)
})

watch([() => props.lat, () => props.lon], ([newLat, newLon]) => {
  if (map && props.lat && props.lon) {
    map.setCenterAnimated(new mapkit.Coordinate(newLat, newLon))
  }
})
</script>

<template>
  <div id="map" class="map" :class="{ map_desktop: desktop }"></div>
</template>

<style lang="scss">
.map {
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  overflow: hidden;

  &_desktop {
    height: 100%;
    border-radius: 16px;
  }
}

.landmark {
  padding: 0.4375rem 0 0 0;
  background: rgba(247, 247, 247);
  border-radius: 0.3125rem;
  box-shadow: 0.625rem 0.625rem 3.125rem rgba(0, 0, 0, 0.29);
  font-family: Helvetica, Arial, sans-serif;
  transform-origin: 0 0.625rem;

  h1 {
    margin-top: 0;
    padding: 0.3125rem 0.9375rem;
    background: $primary-green;
    color: rgba(255, 255, 255);
    @include h4-8;
  }

  section {
    padding: 0 0.9375rem 0.3125rem;
    font-size: 0.875rem;
    @include h5-2;
  }

  section p {
    margin: 0.3125rem 0;
  }

  &:after {
    content: '';
    position: absolute;
    top: 0.4375rem;
    left: -1.25rem;
    width: 0;
    height: 0;
    margin-bottom: -0.8125rem;
    border-right: 1.3125rem solid $primary-green;
    border-top: 1.3125rem solid rgba(0, 0, 0, 0);
    border-bottom: 1.3125rem solid rgba(0, 0, 0, 0);
  }
}

@-webkit-keyframes scale-and-fadein {
  0% {
    -webkit-transform: scale(0.2);
    opacity: 0;
  }

  100% {
    -webkit-transform: scale(1);
    opacity: 1;
  }
}

@keyframes scale-and-fadein {
  0% {
    transform: scale(0.2);
    opacity: 0;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
